import {
  PAYMENT_METHODS,
  type PaymentMethod,
} from '@monorepo/shared/schemas/common'
import type { Invoice } from '@monorepo/shared/schemas/invoices'
import { useEffect, useState } from 'react'
import { formatCentsAsCurrency, paymentsService } from '@/features/invoices'
import { toast } from '@/lib/toast'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Textarea } from '@/shared/components/ui/textarea'

const formatLabel = (value: string) =>
  value
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')

interface RecordPaymentDialogProps {
  invoice: Invoice | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

export function RecordPaymentDialog({
  invoice,
  open,
  onOpenChange,
  onSuccess,
}: RecordPaymentDialogProps) {
  const [paymentAmount, setPaymentAmount] = useState('')
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | ''>('')
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(false)

  // Initialize amount when invoice changes
  useEffect(() => {
    if (invoice) {
      setPaymentAmount((invoice.amount_due_cents / 100).toFixed(2))
      setPaymentMethod('')
      setNotes('')
    }
  }, [invoice])

  const handleSubmit = async () => {
    if (!invoice || !paymentAmount || !paymentMethod) {
      return
    }

    setLoading(true)
    try {
      await paymentsService.recordPayment({
        invoice_id: invoice.id,
        amount: Math.round(Number.parseFloat(paymentAmount) * 100),
        currency: invoice.currency,
        payment_method: paymentMethod,
        notes: notes || undefined,
      })
      toast.success('Payment recorded successfully')
      onOpenChange(false)
      onSuccess()
    } catch {
      toast.error('Failed to record payment')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setPaymentAmount('')
    setPaymentMethod('')
    setNotes('')
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
          <DialogDescription>
            {invoice && (
              <div>
                {invoice.customer?.name && (
                  <>
                    Record a payment for{' '}
                    <strong>{invoice.customer.name}</strong>
                    <br />
                  </>
                )}
                Invoice: {invoice.invoice_number}
                <br />
                Remaining:{' '}
                {formatCentsAsCurrency(
                  invoice.amount_due_cents,
                  invoice.currency
                )}
              </div>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-4'>
          <div className='space-y-2'>
            <Label htmlFor='payment-amount'>Payment Amount ($)</Label>
            <Input
              id='payment-amount'
              type='number'
              step='0.01'
              min='0'
              value={paymentAmount}
              onChange={e => setPaymentAmount(e.target.value)}
              placeholder='0.00'
            />
          </div>

          <div className='space-y-2'>
            <Label htmlFor='payment-method'>Payment Method</Label>
            <Select
              value={paymentMethod}
              onValueChange={value => setPaymentMethod(value as PaymentMethod)}
            >
              <SelectTrigger>
                <SelectValue placeholder='Select payment method' />
              </SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map(method => (
                  <SelectItem key={method} value={method}>
                    {formatLabel(method)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='payment-notes'>Notes (optional)</Label>
            <Textarea
              id='payment-notes'
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder='Payment notes...'
              rows={2}
            />
          </div>

          <div className='flex justify-end gap-3 pt-4'>
            <Button variant='outline' onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!paymentAmount || !paymentMethod || loading}
            >
              {loading ? 'Recording...' : 'Record Payment'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
